import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowRight,
  CheckCircle2,
  Target,
  FileCheck,
  Languages,
  MessageSquare,
  Phone,
  Download
} from 'lucide-react';

export const LaunchProgramPage = () => {
  const phases = [
    {
      number: '01',
      title: 'Regulatory Strategy & Market Mapping',
      subtitle: 'What This Phase Does',
      description: 'Helps you identify the right markets for your products and understand regulatory expectations before starting the documentation process.',
      color: '#606090',
      icon: Target,
      inputs: [
        'Product list',
        'Composition details',
        'Available documents (COA, COA-API, pack insert, label, artwork, stability data etc.)',
        'Target regions (optional)'
      ],
      outputs: [
        'Country-by-country feasibility mapping',
        'Regulatory pathway identification (drug / supplement / food / device category)',
        'Product shortlist based on documentation readiness',
        'Market selection strategy for LATAM, GCC, Africa & Asia',
        'Document checklist for each shortlisted country'
      ],
      outcome: 'Clear understanding of where your products can be registered fastest and with the least documentation gaps.'
    },
    {
      number: '02',
      title: 'Dossier Compilation (CTD/ACTD/API/DMF)',
      subtitle: 'What This Phase Does',
      description: 'Converts your raw documents into a regulator-ready dossier in the correct structure (CTD, ACTD, country-custom formats).',
      color: '#7090A0',
      icon: FileCheck,
      inputs: [
        'Technical data, COAs, specifications',
        'API documents / DMF details',
        'English pack inserts, formulation sheets',
        'Manufacturing SOPs, QA/QC data',
        'Artwork files (if available)'
      ],
      outputs: [
        'Module 1–5 CTD or ACTD dossier',
        'Country-adapted variations',
        'Technical write-ups (QOS, summaries, formulation rationale)',
        'Integrated API + finished product documentation',
        'Full document indexing & QA review',
        'Final digital dossier in MOH-ready format'
      ],
      outcome: 'A complete, submission-ready document set that importer and regulators receive with zero confusion.'
    },
    {
      number: '03',
      title: 'Labels, Artworks & Translations',
      subtitle: 'What This Phase Does',
      description: 'Ensures your artwork complies with country rules, mandatory statements, and language requirements.',
      color: '#A060B0',
      icon: Languages,
      inputs: [
        'Current artwork',
        'Labels, box design',
        'Marketing claims',
        'Existing translations (if any)'
      ],
      outputs: [
        'Artwork / label compliance report',
        'Mandatory text additions',
        'Claim corrections / approved claims only',
        'Pack insert formatting + content compliance',
        'Translations (Arabic, French, Burmese, Spanish, Portuguese)',
        'Export-ready final artwork files'
      ],
      outcome: 'No last-minute artwork corrections during submission — regulators get exactly what they expect.'
    },
    {
      number: '04',
      title: 'Deficiency Handling & Query Response',
      subtitle: 'What This Phase Does',
      description: 'Supports you and your importer through clarifications, document additions, and regulator queries after submission.',
      color: '#708080',
      icon: MessageSquare,
      inputs: [
        'MOH query letter',
        'Submission file copy',
        'Additional data requested by authority'
      ],
      outputs: [
        'Full analysis of each query',
        'Document / evidence preparation',
        'Scientific justification drafts',
        'Revised artwork or updated documents',
        'Follow-up notes for importer to file',
        'Support until acceptance or closure of query'
      ],
      outcome: 'Faster approvals, fewer delays, and stronger alignment with regulator expectations.'
    }
  ];

  const benefits = [
    {
      title: 'Eliminates Rework',
      description: 'Every phase ensures the next stage is prepared correctly.'
    },
    {
      title: 'Reduces Deficiencies',
      description: 'A complete QC process before importer submission.'
    },
    {
      title: 'Region-Specific Formatting',
      description: 'GCC formatting ≠ African formatting ≠ LATAM formatting.'
    },
    {
      title: 'Direct Support for Importers',
      description: 'We coordinate with them to ensure correct filing.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Regulatory Launch Program | End-to-End Pharma & Nutra Registration</title>
        <meta
          name="description"
          content="Discover TheApostrophe's 4-phase Regulatory Launch Program for pharma and nutraceutical exports. Strategy, dossier development, artwork compliance, and MOH query handling for LATAM, GCC, Africa & Asia."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2 text-sm" style={{ backgroundColor: '#A060B0', color: 'white' }}>
            Our Signature Program
          </Badge>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" style={{ color: '#303040' }}>
            The Regulatory Launch Program
          </h1>
          <p className="text-3xl md:text-4xl font-bold mb-8" style={{ color: '#606090' }}>
            A Four-Phase System for Predictable Global Approvals
          </p>
          <p className="text-xl mb-12 leading-relaxed max-w-4xl mx-auto" style={{ color: '#708080' }}>
            A structured, end-to-end regulatory pathway aligning your product with the expectations of regulators across LATAM, GCC, Africa & Asia.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: '#606090', color: 'white' }}
            >
              Learn How It Works
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Why Structured Program */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(96, 96, 144, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Why a Structured Program?
          </h2>
          <p className="text-xl leading-relaxed text-center" style={{ color: '#708080' }}>
            Regulatory submissions succeed when documentation is organized, complete, and tailored to the expectations of each country's authority.
            The Regulatory Launch Program provides a step-by-step, senior-led framework that eliminates guesswork, reduces deficiencies, and ensures every file is submission-ready before reaching your importer or Ministry of Health.
          </p>
        </div>
      </section>

      {/* The 4 Phases - Visual Timeline */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-20 text-center" style={{ color: '#303040' }}>
            The 4-Phase Journey
          </h2>

          {phases.map((phase, index) => (
            <div key={index} className="mb-20">
              {/* Phase Header */}
              <div className={`flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} items-center gap-12 mb-12`}>
                <div className="flex-1">
                  <div className="flex items-center gap-6 mb-6">
                    <div
                      className="w-24 h-24 rounded-2xl flex items-center justify-center text-4xl font-bold text-white shadow-lg"
                      style={{ backgroundColor: phase.color }}
                    >
                      {phase.number}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-3xl font-bold mb-2" style={{ color: '#303040' }}>
                        {phase.title}
                      </h3>
                      <p className="text-lg" style={{ color: '#708080' }}>
                        {phase.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: phase.color + '20' }}
                >
                  <phase.icon className="h-10 w-10" style={{ color: phase.color }} />
                </div>
              </div>

              {/* Phase Details - Accordion */}
              <div className="max-w-5xl" style={{ marginLeft: index % 2 === 0 ? '0' : 'auto' }}>
                <Accordion type="single" collapsible className="space-y-4">
                  {/* Inputs */}
                  <AccordionItem
                    value={`inputs-${index}`}
                    className="border-0 rounded-2xl overflow-hidden shadow-md"
                    style={{ backgroundColor: 'white' }}
                  >
                    <AccordionTrigger className="px-8 py-6 hover:no-underline">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: phase.color + '20' }}
                        >
                          <ArrowRight className="h-5 w-5" style={{ color: phase.color }} />
                        </div>
                        <span className="text-lg font-bold" style={{ color: '#303040' }}>
                          Inputs (Client Provides)
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-8 pb-6">
                      <ul className="space-y-3">
                        {phase.inputs.map((input, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div
                              className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                              style={{ backgroundColor: phase.color }}
                            />
                            <span style={{ color: '#708080' }}>{input}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Outputs */}
                  <AccordionItem
                    value={`outputs-${index}`}
                    className="border-0 rounded-2xl overflow-hidden shadow-md"
                    style={{ backgroundColor: 'white' }}
                  >
                    <AccordionTrigger className="px-8 py-6 hover:no-underline">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: phase.color + '20' }}
                        >
                          <CheckCircle2 className="h-5 w-5" style={{ color: phase.color }} />
                        </div>
                        <span className="text-lg font-bold" style={{ color: '#303040' }}>
                          Outputs (We Deliver)
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-8 pb-6">
                      <ul className="space-y-3">
                        {phase.outputs.map((output, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: phase.color }} />
                            <span style={{ color: '#708080' }}>{output}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {/* Outcome Badge */}
                <div className="mt-6 p-6 rounded-2xl" style={{ backgroundColor: phase.color + '10' }}>
                  <div className="flex items-start gap-3">
                    <Target className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: phase.color }} />
                    <div>
                      <h4 className="font-bold mb-2" style={{ color: phase.color }}>
                        Outcome:
                      </h4>
                      <p className="text-lg" style={{ color: '#708080' }}>
                        {phase.outcome}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Connector Line */}
              {index < phases.length - 1 && (
                <div className="flex justify-center my-12">
                  <div className="w-1 h-16 rounded-full" style={{ backgroundColor: phases[index + 1].color }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Program Benefits */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(112, 128, 128, 0.05)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            What Makes Our Program Effective?
          </h2>
          <div className="space-y-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-6 p-8 rounded-2xl transition-all duration-300 hover:shadow-lg"
                style={{ backgroundColor: 'white' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl font-bold"
                  style={{ backgroundColor: '#A060B0', color: 'white' }}
                >
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#303040' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-lg" style={{ color: '#708080' }}>
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#303040' }}>
            Start Your Regulatory Journey with Clarity and Confidence
          </h2>
          <p className="text-xl mb-12" style={{ color: '#708080' }}>
            Let's map your product portfolio to the right markets and build a submission-ready regulatory roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
              >
                Book a Call
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
              style={{ borderColor: '#606090', color: '#606090' }}
            >
              Download 4-Phase Overview
              <Download className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
